import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Search, Edit2, Shield, UserX, UserCheck, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { readItems } from '@/lib/api/normalize';
import { useApp } from '@/contexts/AppContext';
import { TableSkeleton } from '@/components/ui/skeletons';

export default function AdminUsers() {
  const { t } = useTranslation();
  const { user: currentUser, lang } = useApp();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api<any>(`/admin/users?search=${encodeURIComponent(search)}`, { auth: true });
      setUsers(readItems(res));
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleStatus = async (user: any) => {
    const nextStatus = !user.is_active;
    try {
      await api(`/admin/users/${user.id}/status`, {
        method: 'PUT',
        body: { is_active: nextStatus },
        auth: true
      });
      toast.success(nextStatus ? 'User activated' : 'User deactivated');
      loadUsers();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update user status');
    }
  };

  const handleRoleUpdate = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await api(`/admin/users/${editingUser.id}/role`, {
        method: 'PUT',
        body: { role: selectedRole },
        auth: true
      });
      toast.success('User role updated');
      setEditingUser(null);
      loadUsers();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update user role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user: any) => {
    if (!confirm(`Are you sure you want to delete ${user.full_name || user.email}?`)) return;
    try {
      await api(`/admin/users/${user.id}`, {
        method: 'DELETE',
        auth: true
      });
      toast.success('User deleted successfully');
      loadUsers();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete user');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{t('admin.usersPage.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('admin.usersPage.subtitle')}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            placeholder={t('admin.usersPage.searchPlaceholder')}
          />
        </div>
        <Button onClick={loadUsers} variant="outline">{t('admin.usersPage.refresh')}</Button>
      </div>

      {loading ? (
        <TableSkeleton rows={5} cols={3} />
      ) : (
        <div className="space-y-4">
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg overflow-hidden bg-card text-card-foreground shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-start p-3">{t('admin.usersPage.userDetails')}</th>
                  <th className="text-start p-3">{t('admin.usersPage.role')}</th>
                  <th className="text-end p-3">{t('admin.common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b hover:bg-muted/30 transition-colors">
                    <td className="p-3">
                      <span className="block font-semibold">{u.full_name || t('admin.usersPage.noName')}</span>
                      <span className="block text-xs text-muted-foreground">{u.email}</span>
                      {u.phone && <span className="block text-[11px] text-muted-foreground mt-0.5">📞 {u.phone}</span>}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                        u.role === 'super_admin' ? 'bg-red-500/10 text-red-500' :
                        u.role === 'admin' ? 'bg-amber-500/10 text-amber-500' :
                        u.role === 'operator' ? 'bg-blue-500/10 text-blue-500' :
                        'bg-green-500/10 text-green-500'
                      }`}>
                        <Shield className="w-3 h-3" />
                        {u.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="p-3 text-end space-x-1">
                      {u.id === currentUser?.id ? (
                        <span className="text-xs text-muted-foreground font-medium pr-3">
                          {t('admin.usersPage.you')}
                        </span>
                      ) : (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => { setEditingUser(u); setSelectedRole(u.role); }}
                            title={t('admin.usersPage.changeRole')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteUser(u)}
                            title={t('admin.usersPage.deleteUser')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card Grid View */}
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {users.map(u => (
              <div key={u.id} className="surface-card rounded-xl border border-border p-4 space-y-3 bg-card shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="block font-bold text-base leading-snug">{u.full_name || t('admin.usersPage.noName')}</span>
                    <span className="block text-xs text-muted-foreground break-all">{u.email}</span>
                    {u.phone && <span className="block text-xs text-muted-foreground mt-1">📞 {u.phone}</span>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider capitalize ${
                      u.role === 'super_admin' ? 'bg-red-500/10 text-red-500' :
                      u.role === 'admin' ? 'bg-amber-500/10 text-amber-500' :
                      u.role === 'operator' ? 'bg-blue-500/10 text-blue-500' :
                      'bg-green-500/10 text-green-500'
                    }`}>
                      <Shield className="w-3 h-3" />
                      {u.role.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1 border-t pt-2.5">
                  {u.id === currentUser?.id ? (
                    <span className="text-xs text-muted-foreground font-medium py-1.5">
                      {t('admin.usersPage.you')}
                    </span>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditingUser(u); setSelectedRole(u.role); }}
                        className="flex items-center gap-1 h-9 px-3"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Role
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:bg-destructive/10 h-9 px-3"
                        onClick={() => handleDeleteUser(u)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {users.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">No users found.</div>
          )}
        </div>
      )}

      {/* Role Editor Dialog */}
      <Dialog open={!!editingUser} onOpenChange={v => !v && setEditingUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label>Select Role for {editingUser?.full_name || editingUser?.email}</Label>
              <div className="mt-2">
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Client (Customer)</SelectItem>
                    <SelectItem value="admin">Admin (Dashboard)</SelectItem>
                    <SelectItem value="super_admin">Super Admin (Owner)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={handleRoleUpdate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
